import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '../../../environment';
import { BisnodeStatus } from '../../models/bisnode-status.model';
import { CreateWorksiteModel } from '../../models/create-worksite.model';
import { ListCompany } from '../../models/list-company.model';
import { ListWorksite } from '../../models/list-worksite.model';
import { SearchResult } from '../../models/search-result.model';
import { WorksiteDetail } from '../../models/worksite-detail.model';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
    Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class WorksiteClient {
  private http = inject(HttpClient);
  private env = inject(environment);
  private apiUrl = this.env.oregApiUrl + '/worksite';

  public searchWorksites(
    companyNumber?: string,
    name?: string,
    city?: string,
    country?: string,
    worksiteNumber?: string,
    companyId?: string,
  ): Observable<SearchResult<ListCompany>> {
    const searchParameters = [
      companyNumber && companyNumber.trim()
        ? `companyNumber=${companyNumber.trim()}`
        : undefined,
      name && name.trim() ? `name=${name.trim()}` : undefined,
      city && city.trim() ? `city=${city.trim()}` : undefined,
      country && country.trim() ? `country=${country.trim()}` : undefined,
      worksiteNumber && worksiteNumber.trim()
        ? `worksiteNumber=${worksiteNumber.trim()}`
        : undefined,
      companyId && companyId.trim() ? `companyId=${companyId}` : undefined,
    ]
      .filter(Boolean)
      .join('&');

    const url = `${this.apiUrl}/search?${searchParameters}`;

    return this.http.get<SearchResult<ListCompany>>(url, httpOptions).pipe(
      map((data) => {
        const companies = data.result.map((company) => {
          if (!company.bisnodeStatus) {
            company.bisnodeStatus = {
              name: 'Aktiv',
              code: 'ST00',
            } as BisnodeStatus;
          }
          company.worksites = company.worksites.map((worksite) => {
            if (
              worksite.identicalWorksites &&
              worksite.identicalWorksites.length > 0
            ) {
              worksite.identicalWorksites = worksite.identicalWorksites.map(
                (id) => {
                  const identicalWorksite = {} as ListWorksite;
                  identicalWorksite.providerId = id;
                  return identicalWorksite;
                },
              );
              worksite.hasIdenticalWorksites = true;
            }
            return worksite;
          });

          return company;
        });

        const result = {} as SearchResult<ListCompany>;
        result.result = companies;
        result.totalAmount = data.totalAmount;
        result.bisnodeNotResponding = data.bisnodeNotResponding;
        return result;
      }),
    );
  }

  public selectWorksite(
    worksiteProvider: string,
    worksiteId: string,
  ): Observable<WorksiteDetail> {
    const url = `${this.apiUrl}/select?worksiteProvider=${worksiteProvider}&worksiteId=${worksiteId}`;
    return this.http.get<WorksiteDetail>(url, httpOptions).pipe(
      catchError(() => {
        return of(undefined);
      }),
    );
  }

  public extendWorksites(ids: string[]): Observable<ListWorksite[]> {
    const url = `${this.apiUrl}/extend`;
    const data = JSON.stringify(ids);

    return this.http.post<ListWorksite[]>(url, data, httpOptions);
  }

  public createWorksite(model: CreateWorksiteModel): Observable<ListWorksite> {
    const url = `${this.apiUrl}`;
    const data = JSON.stringify(model);

    return this.http.post<ListWorksite>(url, data, httpOptions);
  }

  public getWorksite(id: string): Observable<WorksiteDetail> {
    return this.http.get<WorksiteDetail>(`${this.apiUrl}/${id}`);
  }
}
